import { useNavigate } from 'react-router-dom';
import { Store } from 'lucide-react';
import { EnterpriseEndpoints } from '../../../utils/endpoints';
import { formatLagos } from '../../../utils/datetime';
import { formatCount, formatNaira } from '../../../utils/money';
import { useServerTable } from '../../../hooks/useServerTable';
import PageHeader from '../../shell/PageHeader';
import {
  Card,
  DataTable,
  DetailRow,
  FilterSelect,
  Pagination,
  SearchInput,
  StatCard,
  StatusPill,
  TableCard,
} from '../../ui';
import EnterpriseDeactivated, { isEnterpriseDeactivated } from './enterprise/EnterpriseDeactivated';

const breadcrumbs = [{ label: 'Home', to: '/layout' }, { label: 'Merchants' }];
const STORE_STATUSES = ['active', 'inactive'];
const PAGE_SIZES = [10, 20, 50];

const merchantPath = (id) => `/layout/enterprise-merchants/${id}`;

const SettlementAccountCard = ({ account, loading }) => (
  <Card title="Settlement account" className="mb-0">
    <DetailRow label="Bank" value={account && (account.bank_name ?? account.bank_code)} loading={loading} />
    <DetailRow label="Account name" value={account?.account_name} loading={loading} />
    <DetailRow label="Account number" value={account?.account_number} loading={loading} />
  </Card>
);

const EnterpriseMerchants = () => {
  const navigate = useNavigate();

  const table = useServerTable(EnterpriseEndpoints.merchants, {
    pageSize: 20,
    filters: { status: '' },
    fallbackError: 'We could not load your merchants. Please try again.',
  });

  if (isEnterpriseDeactivated(table.requestError)) {
    return (
      <>
        <PageHeader title="Merchants" breadcrumbs={breadcrumbs} />
        <EnterpriseDeactivated />
      </>
    );
  }

  const summary = table.envelope?.summary;
  const summaryLoading = !summary && table.loading;

  const columns = [
    {
      key: 'name',
      header: 'Name',
      render: (store) => (
        <span className="font-medium text-theme-headings dark:text-themedark-headings">{store.name}</span>
      ),
    },
    {
      key: 'account_number',
      header: 'Account number',
      className: 'whitespace-nowrap',
      render: (store) => store.account_number || '—',
    },
    { key: 'balance', header: 'Balance', align: 'right', render: (store) => formatNaira(store.balance) },
    {
      key: 'total_tx_value',
      header: 'Total tap value',
      align: 'right',
      render: (store) => formatNaira(store.total_tx_value),
    },
    {
      key: 'discount_total',
      header: 'Discount',
      align: 'right',
      render: (store) => formatNaira(store.discount_total),
    },
    {
      key: 'status',
      header: 'Status',
      align: 'center',
      render: (store) => <StatusPill status={store.status} />,
      filter: (
        <FilterSelect
          value={table.filters.status ?? ''}
          onChange={(value) => table.setFilter('status', value)}
          options={STORE_STATUSES}
          label="All"
          ariaLabel="Filter by status"
        />
      ),
    },
    {
      key: 'created_at',
      header: 'Created (WAT)',
      render: (store) => <span className="whitespace-nowrap">{formatLagos(store.created_at)}</span>,
    },
  ];

  return (
    <>
      <PageHeader title="Merchants" breadcrumbs={breadcrumbs} />

      <div className="mb-6 grid gap-6 md:grid-cols-2">
        <StatCard
          metric={{ label: 'Total stores', value: summary?.total_stores, unit: 'count' }}
          icon={<Store className="h-5 w-5" />}
        />
        <SettlementAccountCard account={summary?.settlement_account} loading={summaryLoading} />
      </div>

      <TableCard
        title="Merchants"
        summary={{ label: 'Total', value: formatCount(table.total), loading: table.loading }}
        toolbar={
          <SearchInput
            value={table.search}
            onChange={table.setSearch}
            placeholder="Search name, registration or account number"
          />
        }
        footer={
          table.total > 0 && (
            <Pagination
              page={table.page}
              pageCount={table.pageCount}
              pageSize={table.pageSize}
              pageSizeOptions={PAGE_SIZES}
              onPageChange={table.setPage}
              onPageSizeChange={table.setPageSize}
            />
          )
        }
      >
        <DataTable
          columns={columns}
          rows={table.rows}
          rowKey={(store) => store.id}
          serial
          serialStart={table.page * table.pageSize}
          loading={table.loading}
          error={table.error}
          empty={{ title: 'No merchants', message: 'No merchants match these filters.' }}
          onRowClick={(store) => navigate(merchantPath(store.id))}
        />
      </TableCard>
    </>
  );
};

export default EnterpriseMerchants;
